import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
} from '@material-ui/core';
import i18n from 'i18n-js';

import styles from '../../styles';
import DateFilters from '../DateFIlters';
import TotalAmount from '../TotalAmount';
import { DateHistoryData } from '../finances';
import agent from 'api/agent';
import globalStore from 'store/globalStore';
import { createDateIgnoreTimezone, formatDateTimeGermany } from 'helpers/datetimeHelper';
import { ApplicationState } from 'store';

interface StornoData {
    id: string;
    name: string;
    orderDate: number;
    quantity: number;
    amount: number;
    reason: string;
    waiterName: string;
    userName: string;
    stornoDate: number;
}

type OrderBy =
    | 'name'
    | 'orderDate'
    | 'quantity'
    | 'amount'
    | 'reason'
    | 'waiterName'
    | 'userName'
    | 'stornoDate';

export default function Storno() {
    const classes = styles();
    const dispatch = useDispatch();
    const currency = useSelector(
        (state: ApplicationState) =>
            state.restaurantDetails?.restaurantInformation?.paymentDetails?.currency || 'CHF'
    );
    const [dateFilter, setDateFilter] = useState(0);
    const [selectedDatesHistory, setSelectedDatesHistory] = useState<DateHistoryData[]>([]);
    const [stornoData, setStornoData] = useState<StornoData[]>([]);
    const [orderBy, setOrderBy] = useState<OrderBy>('name');
    const [asc, setAsc] = useState(true);

    useEffect(() => {
        getData(1);
    }, []);

    function getData(
        dayAgo: number,
        dateHistoryList?: DateHistoryData[],
        dateFrom?: Date,
        dateTo?: Date
    ) {
        if (!!!dateFrom && !!!dateTo) {
            dateFrom = new Date();
            dateTo = new Date();
            dateFrom.setDate(dateFrom.getDate() - dayAgo);
        }
        dispatch(globalStore.actionCreators.showSpiner());
        console.log(dateTo);
        agent.Restaurants.GetStornoData(dateFrom, dateTo)
            .then((data) => {
                setStornoData(data);
            })
            .catch((error) => {
                dispatch(globalStore.actionCreators.showToaster('error', error.message));
            })
            .finally(() => {
                dispatch(globalStore.actionCreators.hideSpiner());
            });

        dateHistoryList && setSelectedDatesHistory(dateHistoryList);
    }

    function handleOrder(field: OrderBy) {
        const isAsc = orderBy === field && asc;
        setOrderBy(field);
        setAsc(!isAsc);
    }

    function getSortedData() {
        return [...stornoData].sort((a, b) => {
            let aValue: number | string;
            let bValue: number | string;

            switch (orderBy) {
                case 'name':
                    aValue = a.name?.toLowerCase();
                    bValue = b.name?.toLowerCase();
                    break;
                case 'quantity':
                    aValue = a.quantity;
                    bValue = b.quantity;
                    break;
                case 'orderDate':
                    aValue = a.orderDate;
                    bValue = b.orderDate;
                    break;
                case 'amount':
                    aValue = a.amount;
                    bValue = b.amount;
                    break;
                case 'reason':
                    aValue = a.reason?.toLowerCase();
                    bValue = b.reason?.toLowerCase();
                    break;
                case 'waiterName':
                    aValue = a.waiterName?.toLowerCase();
                    bValue = b.waiterName?.toLowerCase();
                    break;
                case 'userName':
                    aValue = a.userName?.toLowerCase();
                    bValue = b.userName?.toLowerCase();
                    break;
                case 'stornoDate':
                    aValue = a.stornoDate;
                    bValue = b.stornoDate;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return asc ? -1 : 1;
            }
            if (aValue > bValue) {
                return asc ? 1 : -1;
            }
            return 0;
        });
    }

    return (
        <div>
            <div className={classes.topContainer} style={{ justifyContent: 'flex-end' }}>
                <TotalAmount
                    style={{ position: 'static' }}
                    currency={currency}
                    title={i18n.t('common.total')}
                    amount={stornoData
                        .reduce((sum, item) => sum + item.amount * item.quantity, 0)
                        .toFixed(2)}
                />
            </div>

            <div className={`${classes.flexRowEnd} ${classes.p15}`}>
                <div className={classes.flexRowSpaceBetween} style={{ width: '100%' }}>
                    <DateFilters
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        getData={getData}
                        selectedDatesHistory={selectedDatesHistory}
                        setSelectedDatesHistory={setSelectedDatesHistory}
                    />
                </div>
            </div>

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table aria-label="storno table" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                            >
                                <TableSortLabel
                                    active={orderBy === 'orderDate'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('orderDate')}
                                >
                                    {i18n.t('common.orderDate')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                                style={{ maxWidth: '200px' }}
                            >
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('name')}
                                >
                                    {i18n.t('common.itemName')}
                                </TableSortLabel>
                            </TableCell>

                            <TableCell
                                align="right"
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                            >
                                <TableSortLabel
                                    active={orderBy === 'quantity'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('quantity')}
                                >
                                    {i18n.t('common.quantity')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                            >
                                <TableSortLabel
                                    active={orderBy === 'amount'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('amount')}
                                >
                                    {i18n.t('common.amountCHF')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                            >
                                <TableSortLabel
                                    active={orderBy === 'reason'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('reason')}
                                >
                                    {i18n.t('common.stornoReason')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                style={{ paddingRight: 12, paddingBottom: 16 }}
                                className={classes.tableHeader}
                                sortDirection={asc ? 'asc' : 'desc'}
                            >
                                <TableSortLabel
                                    active={orderBy === 'waiterName'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('waiterName')}
                                >
                                    {i18n.t('common.waiterName')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                style={{ paddingRight: 12, paddingBottom: 16 }}
                                className={classes.tableHeader}
                                sortDirection={asc ? 'asc' : 'desc'}
                            >
                                <TableSortLabel
                                    active={orderBy === 'userName'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('userName')}
                                >
                                    {i18n.t('common.stornoBy')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                style={{ paddingRight: 12, paddingBottom: 16 }}
                                className={classes.tableHeader}
                                sortDirection={asc ? 'asc' : 'desc'}
                            >
                                <TableSortLabel
                                    active={orderBy === 'stornoDate'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('stornoDate')}
                                >
                                    {i18n.t('common.stornoDate')}
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {getSortedData().map((item, i) => (
                            <TableRow key={item.id + i}>
                                <TableCell align="right">
                                    {formatDateTimeGermany(
                                        createDateIgnoreTimezone(item.orderDate)
                                    )}
                                </TableCell>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    style={{
                                        maxWidth: '200px',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'pre-line',
                                    }}
                                >
                                    {item.name}
                                </TableCell>

                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">{item.amount.toFixed(2)}</TableCell>
                                <TableCell align="right">{item.reason}</TableCell>
                                <TableCell align="right">{item.waiterName}</TableCell>
                                <TableCell align="right">{item.userName}</TableCell>
                                <TableCell align="right">
                                    {formatDateTimeGermany(
                                        createDateIgnoreTimezone(item.stornoDate)
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}
